// Hasil baca AI (rincian barang) untuk versi mockup ini — dilihat desainer sebelum FIX. Tidak menjalankan AI.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

const ICON = { ok: CheckCircle2, diff: XCircle, check: AlertTriangle };

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai-mockup-read/file`;

export function MockupAiRead({ fileId }) {
  const [d, setD] = useState(null), [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const r = await axios.get(`${API}/${fileId}`, { withCredentials: true }); setD(r.data); }
    catch { setD({ error: true }); } finally { setBusy(false); }
  };
  const rd = d?.read;
  return <div className="mai-box" data-testid={`mockup-ai-read-${fileId}`}>
    <button type="button" className="soft-btn small-btn" onClick={load} disabled={busy} data-testid={`mockup-ai-read-btn-${fileId}`}>
      {busy ? "Memuat…" : d ? "Muat ulang hasil AI" : "Lihat hasil AI"}</button>
    {d?.error && <p className="muted small">Gagal memuat. Proses manual tetap berjalan.</p>}
    {d && !d.error && d.enabled === false && <p className="muted small">Pembacaan AI sedang nonaktif.</p>}
    {d && !d.error && d.enabled && !rd && <p className="muted small" data-testid="mockup-ai-read-none">Belum ada hasil AI untuk versi ini (masih menunggu, atau nomor pesanan pada gambar berbeda — tandai Sudah Diperiksa bila gambar sudah benar).</p>}
    {rd && <div className="mai-res">
      <p className="small"><b data-testid="mockup-ai-read-status">{rd.status}</b>{rd.reason ? ` — ${rd.reason}` : ""}</p>
      {(rd.rows || []).map((r, i) => <p key={i} className="small" data-testid={`mockup-ai-read-row-${i}`}>
        {r.product || "—"} · {r.color || "—"} · {r.sleeve || "—"} · {r.size || "—"} · <b>{r.qty || 0} pcs</b>{r.status && r.status !== "OK" ? ` · ${r.status}` : ""}</p>)}
      {(rd.warnings || []).map((w, i) => <p key={`w${i}`} className="muted small">{w}</p>)}
      <p className="muted small">AI hanya membantu — desainer tetap memastikan mockup sebelum FIX.</p>
    </div>}
  </div>;
}

// ---- Kartu utama Desainer: status hasil AI per versi mockup terbaru (data tersimpan, tanpa memanggil Gemini) ----
export const currentFileIds = (job) => (job.mockups || []).filter(m => !m.deleted_at)
  .map(m => (m.versions?.[m.current_version - 1] || m.versions?.[m.versions.length - 1])?.file_id).filter(Boolean);

export function useAiFileReads(jobs) {
  const [m, setM] = useState({ enabled: false, files: {} });
  const [tick, setTick] = useState(0);
  const key = jobs.flatMap(currentFileIds).sort().join(",");
  useEffect(() => {
    if (!key) return;
    axios.get(`${API}s`, { withCredentials: true, params: { ids: key } }).then(r => setM(r.data)).catch(() => {});
  }, [key, jobs, tick]);
  m.reload = () => setTick(t => t + 1);  // ikut penyegaran daftar (satu permintaan ringan), tanpa memanggil AI
  return m;
}

export function aiCardLabel(x) {
  if (!x || x.state === "none") return ["Belum Dibaca", "is-wait"];
  if (x.state === "queued") return ["Menunggu", "is-wait"];
  if (x.state === "processing") return ["Sedang Dibaca", "is-wait"];
  if (x.state !== "done") return ["Gagal", "is-bad"];
  if (x.status === "Siap" && (x.rows || []).length) return ["Berhasil", "is-ok"];
  return ["Perlu Dicek", "is-check"];
}

const rowText = (r) => `${r.product || "—"} · ${r.color || "—"} · ${r.sleeve || "—"} · ${r.size || "—"} · ${r.qty || 0} pcs`;

// Kalimat pencocokan (dari server, tidak diubah) → aspek + kalimat singkat untuk karyawan. Hanya tampilan.
const ASPECTS = [[/tambahan|biaya/i, "Item tambahan"], [/titik/i, "Titik bordir"], [/beberapa pekerjaan/i, "Pekerjaan ganda"],
  [/belum tersimpan|belum tersedia/i, "Data Shopee"], [/jumlah pcs/i, "Jumlah"], [/ukuran/i, "Ukuran"], [/warna/i, "Warna"], [/lengan/i, "Lengan"],
  [/produk/i, "Produk"], [/baca ai/i, "Hasil AI"]];
const SHORT = [[/hasil baca ai/i, "Sebagian isi mockup kurang jelas."], [/kelompok ukuran/i, "Variasi Shopee mencantumkan beberapa ukuran."], [/ukuran tidak tertulis/i, "Ukuran tidak tertulis jelas di variasi Shopee."],
  [/produk belum/i, "Nama produk belum cocok pasti."], [/sebelum fitur titik/i, "Belum diperiksa pada mockup versi lama."],
  [/beberapa jumlah titik|beberapa mockup/i, "Ada beberapa desain — hitung titik per desain secara manual."],
  [/teknik sebagian/i, "Teknik bordir/sablon di mockup kurang jelas."], [/titik bordir belum tertulis/i, "Jumlah titik tidak tertulis jelas di variasi Shopee."],
  [/lengan tidak/i, "Lengan tidak tertulis jelas."], [/warna mockup .* catatan/i, "Warna belum cocok pasti dengan catatan pembeli."],
  [/warna tidak dapat/i, "Warna tidak tertulis jelas."], [/beberapa pekerjaan/i, "Satu pesanan punya beberapa pekerjaan — cek jumlah & ukuran manual."],
  [/tambahan|biaya/i, "Ada item tambahan/biaya, tidak dihitung sebagai pcs."], [/belum tersimpan|belum tersedia/i, "Data pesanan Shopee belum tersedia."]];
const cap = (t) => t.charAt(0).toUpperCase() + t.slice(1);
export const aspectOf = (msg) => (ASPECTS.find(([re]) => re.test(msg)) || [null, "Lainnya"])[1];
export const shortCheck = (msg) => (SHORT.find(([re]) => re.test(msg)) || [null, msg.split(" — ")[0]])[1];
export function diffParts(msg) {
  const m = /^([^:]+):\s*Shopee (.+?), mockup (.+?)\.?$/i.exec(msg);
  return m ? { aspect: m[1].replace(/^Jumlah pcs$/i, "Jumlah"), shopee: m[2], mockup: m[3] } : { aspect: aspectOf(msg), shopee: "", mockup: msg };
}
export function okText(msg) {
  const t = msg.replace(/\s*\([^)]*\)/g, "").replace(/\.$/, "");
  const m = /^Jumlah pcs:\s*(\d+)/i.exec(t);
  return m ? `Jumlah ${m[1]} pcs` : t.replace(/:\s*/, " ").replace(/ (panjang|pendek)$/i, (x) => ` ${cap(x.trim())}`);
}

// Satu kesimpulan: status baca AI dulu, lalu pencocokan Data Shopee. Tidak pernah "Sesuai" bila ada yang belum pasti.
const WHY_ASPECT = { produk: "Nama Produk", lengan: "Lengan", jumlah: "Jumlah Pcs", "baris ganda": "Jumlah Pcs", "total tidak cocok": "Jumlah Pcs" };
const SHORT_NAME = { "Nama Produk": "Produk", "Jumlah Pcs": "Jumlah" };
// Kalimat server → satu baris pendek: "<nilai> — Sesuai" / "... — Tidak Sesuai" / "Perlu dicek — <alasan>"
export function aspectLine(a) {
  const t = String(a.text || "").replace(/\.$/, "").replace(/ titik bordir$/i, " titik");
  const ct = a.aspect === "Titik Bordir" ? a.checkout_titik || [] : [];
  const uniq = [...new Set(ct)];
  if (ct.length && a.status === "check") {
    const head = uniq.length === 1 ? `Checkout ${uniq[0]} titik` : `Checkout per item: ${ct.join(" · ")} titik`;
    return /sebelum fitur titik/i.test(a.why || "") ? `${head} · Belum diperiksa` : `${head} · Perlu dicek`;
  }
  if (a.status === "ok") return `${t.replace(/^Sesuai (checkout|catatan pembeli),\s*/i, "").replace(/ dan /g, " · ")} — Sesuai`;
  if (a.status === "diff") {
    const m = /^Checkout (.+), tetapi mockup (.+)$/i.exec(t);
    return `${m ? `Checkout ${m[1]} · Mockup ${m[2]}` : cap(t.replace(/^Tidak sesuai\s*—\s*/i, ""))} — Tidak Sesuai`;
  }
  if (/sebelum fitur titik/i.test(a.why || "")) return "Belum diperiksa — cek titik manual sesuai checkout";
  return `Perlu dicek — ${shortCheck(a.why || "data belum cukup")}`;
}

export function mockupConclusion(states, m, rows = []) {
  if (states.some(l => l === "Gagal")) return { label: "Gagal", cls: "is-bad", issues: [] };
  if (states.some(l => l === "Menunggu" || l === "Sedang Dibaca")) return { label: "Sedang Dibaca", cls: "is-wait", issues: [] };
  if (states.some(l => l === "Belum Dibaca")) return { label: "Belum Dibaca", cls: "is-wait", issues: [] };
  if (m?.aspects) {  // 4 aspek checkout pembeli; keraguan baca AI menurunkan aspek terkait jadi Perlu Dicek (bukan baris ke-5)
    const doubt = new Set(rows.flatMap(r => r.why || []).map(w => WHY_ASPECT[w]).filter(Boolean));
    const aspects = m.aspects.map(a => a.status === "ok" && doubt.has(a.aspect)
      ? { ...a, status: "check", text: "Perlu Dicek.", why: "Hasil baca AI kurang jelas untuk bagian ini." } : a);
    const cnt = (k) => aspects.filter(a => a.status === k).length;
    const d = cnt("diff"), k = cnt("check");
    return { label: d ? `${d} tidak sesuai` : k ? `${k} perlu dicek` : "Sesuai", cls: d ? "is-bad" : k ? "is-check" : "is-ok", aspects,
      counts: { ok: cnt("ok"), check: k, diff: d } };
  }
  const checks = [...(m?.checks || [])];
  if (states.some(l => l === "Perlu Dicek")) checks.unshift("Hasil baca AI: sebagian isi mockup kurang jelas.");
  if (!m) checks.push("Data pesanan Shopee belum tersedia.");
  const diffs = m?.diffs || [];
  const label = diffs.length ? "Ada Perbedaan" : checks.length ? "Perlu Dicek" : "Sesuai";
  return { label, cls: { "Ada Perbedaan": "is-bad", "Perlu Dicek": "is-check", "Sesuai": "is-ok" }[label], diffs, checks, ok: m?.ok || [],
    issues: [...new Set([...diffs, ...checks].map(aspectOf))] };
}

export function CardAiStatus({ job, reads }) {
  const [open, setOpen] = useState(false);
  const [asked, setAsked] = useState({}), [msg, setMsg] = useState("");
  const sent = useRef(new Set());
  const ask = async (f) => {
    if (sent.current.has(f)) return;  // cegah tekan berulang sebelum render ulang
    sent.current.add(f);
    setAsked(a => ({ ...a, [f]: "busy" })); setMsg("");
    try { await axios.post(`${API}/${f}/read`, null, { withCredentials: true }); setAsked(a => ({ ...a, [f]: "queued" }));
      [5000, 15000, 40000].forEach(ms => setTimeout(() => reads.reload?.(), ms)); }
    catch (e) { sent.current.delete(f); setAsked(a => ({ ...a, [f]: "" })); setMsg(e?.response?.data?.detail || "Gagal meminta baca AI. Pekerjaan tetap bisa dilanjutkan."); }
  };
  const fids = currentFileIds(job);
  if (!reads.enabled || !fids.length) return null;
  const files = fids.map(f => {
    const saved = reads.files[f];  // status tersimpan selalu menang; penanda lokal hanya sampai server mencatat
    const x = asked[f] === "queued" && (!saved || ["none", "failed", "skipped"].includes(saved.state)) ? { state: "queued" } : saved;
    return { f, x, label: aiCardLabel(x)[0], rows: x?.rows || [] };
  });
  const m = reads.jobs?.[job.id];
  const c = mockupConclusion(files.map(x => x.label), m, files.flatMap(x => x.rows));
  const n = c.n ?? (c.diffs || []).length + (c.checks || []).length;
  const J = job.id;
  const sources = <div className="cai-sec" data-testid={`card-mock-check-source-${J}`}><b>Sumber</b>
    {files.map(({ f, label, rows }, i) => <span key={f} className="cai-row" data-testid={`card-ai-status-${J}-${i}`}>
      Hasil AI{fids.length > 1 ? ` mockup ${i + 1}` : ""}: {label}{rows.length ? "" : "."}</span>)}
    {files.flatMap(({ f, rows }, i) => rows.map((r, k) => <span key={`${f}-${k}`} className="cai-row" data-testid={`card-ai-row-${J}-${i}-${k}`}>{rowText(r)}</span>))}
    {m?.note && <span className="cai-row" data-testid={`card-mock-check-note-${J}`}>Catatan pembeli: {m.note}</span>}
  </div>;
  if (c.aspects) return <div className="cai cai-v2" data-testid={`card-ai-${J}`} onClick={e => e.stopPropagation()}>
    <div className="cai-head">
      <span><span className="cai-title">Pemeriksaan Mockup</span><span className="cai-sub">Dibandingkan dengan checkout pembeli</span></span>
      <span className={`cai-badge ${c.cls}`} data-testid={`card-mock-check-${J}`}>{c.label}</span>
    </div>
    <div className="cai-lines">
      {c.aspects.map((a, i) => { const Icon = ICON[a.status] || AlertTriangle; return <div key={i} className={`cai-asp is-${a.status}`}
        data-testid={`card-mock-check-aspect-${J}-${i}`}><Icon size={14}/><span><b>{SHORT_NAME[a.aspect] || a.aspect}</b>
        <span className="cai-val">{aspectLine(a)}</span></span></div>; })}
    </div>
    <div className="cai-foot">
      <span data-testid={`card-mock-check-sum-${J}`}>{[c.counts.ok && `${c.counts.ok} sesuai`, c.counts.diff && `${c.counts.diff} tidak sesuai`,
        c.counts.check && `${c.counts.check} perlu dicek`].filter(Boolean).join(" · ")}</span>
      <button type="button" className="link-btn cai-more" onClick={() => setOpen(!open)} data-testid={`card-mock-check-more-${J}`}>
        {open ? "Tutup rincian" : "Lihat rincian"}</button>
    </div>
    {open && <div className="cai-detail" data-testid={`card-mock-check-detail-${J}`}>
      {c.aspects.some(a => a.why) && <div className="cai-sec"><b>Alasan</b>{c.aspects.filter(a => a.why).map((a, i) =>
        <span key={i} className="cai-row" data-testid={`card-mock-check-why-${J}-${i}`}>{SHORT_NAME[a.aspect] || a.aspect}: {a.why}</span>)}</div>}
      {sources}
    </div>}
  </div>;
  return <div className="cai" data-testid={`card-ai-${J}`} onClick={e => e.stopPropagation()}>
    <div className="cai-head">
      <span className="cai-title">Pemeriksaan Mockup</span>
      <span className={`cai-badge ${c.cls}`} data-testid={`card-mock-check-${J}`}>{c.label}</span>
    </div>
    {n > 0 && <span className="cai-sum" data-testid={`card-mock-check-sum-${J}`}>{n} hal perlu diperiksa: {c.issues.join(", ")}</span>}
    {files.map(({ f, x, label }, i) => (label === "Belum Dibaca" || label === "Gagal") && <span key={f} className="cai-act">
      {label === "Gagal" && x?.reason && <span className="cai-reason" data-testid={`card-ai-fail-${J}-${i}`}>Gagal dibaca AI{fids.length > 1 ? ` (mockup ${i + 1})` : ""}.</span>}
      <button type="button" className="link-btn cai-more" disabled={!!asked[f]} onClick={() => ask(f)} data-testid={`card-ai-read-${J}-${i}`}>
        {asked[f] === "busy" ? "Meminta…" : `Baca AI${fids.length > 1 ? ` mockup ${i + 1}` : ""}`}</button></span>)}
    {msg && <span className="cai-reason" data-testid={`card-ai-msg-${J}`}>{msg}</span>}
    <button type="button" className="link-btn cai-more" onClick={() => setOpen(!open)} data-testid={`card-mock-check-more-${J}`}>
      {open ? "Tutup rincian" : "Lihat Rincian"}</button>
    {open && <div className="cai-detail" data-testid={`card-mock-check-detail-${J}`}>
      {(c.checks || []).length > 0 && <div className="cai-sec" data-testid={`card-mock-check-checks-${J}`}><b>Perlu Diperiksa</b>
        {c.checks.map((t, i) => <span key={i} className="cai-row"><b>{aspectOf(t)}:</b> {shortCheck(t)}</span>)}</div>}
      {(c.diffs || []).length > 0 && <div className="cai-sec is-bad" data-testid={`card-mock-check-diffs-${J}`}><b>Ada Perbedaan</b>
        {c.diffs.map((t, i) => { const d = diffParts(t); return <span key={i} className="cai-row"><b>{d.aspect}:</b>{" "}
          {d.shopee ? <>Shopee <b>{d.shopee}</b> · Mockup <b>{d.mockup}</b></> : d.mockup}</span>; })}</div>}
      {(c.ok || []).length > 0 && <div className="cai-sec is-ok" data-testid={`card-mock-check-ok-${J}`}><b>Sudah Sesuai</b>
        <span className="cai-row">{c.ok.map(okText).join(" · ")}</span></div>}
      {sources}
    </div>}
  </div>;
}
